// Helpers for unwrapping the sql.Null* proto wrappers emitted by pangaea.
// Every delivery entity has a handful of nullable columns (description,
// contract_number, end_date, etc.) and the proto representation pairs each
// with a `valid` flag. These helpers collapse the {value, valid} shape into
// the more idiomatic `T | undefined` so component code never branches on
// `valid` directly.
export const nullStr = (v)=>v?.valid ? v.value : undefined;
export const nullInt = (v)=>v?.valid ? Number(v.value) : undefined;
export const nullFloat = (v)=>v?.valid ? v.value : undefined;
export const nullBool = (v)=>v?.valid ? v.value : undefined;
// google.protobuf.Timestamp → ISO string (or undefined if zero).
export const timestampToIso = (v)=>{
    if (!v) return undefined;
    const seconds = typeof v.seconds === 'bigint' ? Number(v.seconds) : v.seconds;
    if (seconds === 0 && v.nanos === 0) return undefined;
    return new Date(seconds * 1000 + Math.floor(v.nanos / 1000000)).toISOString();
};
// Inverse: ISO string → proto Timestamp shape (for mutation payloads).
export const isoToTimestamp = (iso)=>{
    if (!iso) return undefined;
    const ms = new Date(iso).getTime();
    return {
        seconds: BigInt(Math.floor(ms / 1000)),
        nanos: ms % 1000 * 1000000
    };
};
// Parses an <input type="date"> string ("2026-05-14") as local midnight rather
// than UTC midnight. new Date("2026-05-14") treats the value as UTC, which
// shifts business dates by a day for any user west of UTC. Every delivery
// calendar field (DSO period, EAC/CPARS/QASP windows, audit & review dates)
// must round-trip through this helper.
export const parseLocalDate = (ymd)=>{
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d);
};
// Inverse of parseLocalDate — formats a Timestamp (written as local midnight)
// back to a YYYY-MM-DD string in local time. Use whenever the value will be
// shown as a calendar day rather than an instant.
export const formatLocalYmd = (v)=>{
    if (!v) return undefined;
    const seconds = typeof v.seconds === 'bigint' ? Number(v.seconds) : v.seconds;
    if (seconds === 0 && v.nanos === 0) return undefined;
    const d = new Date(seconds * 1000 + Math.floor(v.nanos / 1000000));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// UTC variants for calendar fields whose stored timestamp is anchored at
// UTC-midnight rather than local — notably contract-sync-imported dates, which
// reverie writes via time.Parse("2006-01-02", …) (UTC midnight). Reading those
// through the local helpers shifts the displayed day (and, once editable,
// drifts the stored instant on save) for any user off UTC. These render and
// write the SAME calendar day in every timezone, so a UTC-anchored date-only
// value round-trips unchanged.
export const parseUtcDate = (ymd)=>{
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
};
export const formatUtcYmd = (v)=>{
    if (!v) return undefined;
    const seconds = typeof v.seconds === 'bigint' ? Number(v.seconds) : v.seconds;
    if (seconds === 0 && v.nanos === 0) return undefined;
    const d = new Date(seconds * 1000 + Math.floor(v.nanos / 1000000));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};
// Inverse helpers: build NullX proto shapes from optional frontend values.
export const toNullStr = (v)=>v == null ? {
        value: '',
        valid: false
    } : {
        value: v,
        valid: true
    };
export const toNullInt = (v)=>v == null ? {
        value: BigInt(0),
        valid: false
    } : {
        value: BigInt(v),
        valid: true
    };
export const toNullFloat = (v)=>v == null ? {
        value: 0,
        valid: false
    } : {
        value: v,
        valid: true
    };
export const toNullBool = (v)=>v == null ? {
        value: false,
        valid: false
    } : {
        value: v,
        valid: true
    };
